import { useEffect } from 'react';
import { useParams } from 'react-router';
import { Breadcrumbs, Grid, Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useAccountsByCustomerStore, useTransactionCustomerStore, useTransactionsStore } from '@lib';
import { DateUtils, RoutePaths, usePrevious } from '@shared';
import { DelayedLinearProgress } from '@widgets';

type Params = {
  selectedCustomerId: string;
  selectedAccountId: string;
};

export const TransactionsPage = () => {
  const { selectedAccountId, selectedCustomerId } = useParams<Params>();

  const { customer, getCustomer, clear: clearCustomer } = useTransactionCustomerStore();
  const { account, getAccountsByCustomer } = useAccountsByCustomerStore();
  const { loading, transactions, getTransactions } = useTransactionsStore();

  useEffect(() => {
    if (!selectedCustomerId) return;
    getCustomer(selectedCustomerId);
    getTransactions({ accId: selectedAccountId });

    return () => {
      clearCustomer();
    };
  }, []);

  const prevCustomer = usePrevious(customer);
  useEffect(() => {
    if (!customer) return;
    if (prevCustomer === customer) return;
    getAccountsByCustomer({ customerId: customer.id, accountId: selectedAccountId });
  }, [customer, prevCustomer]);

  return (
    <>
      <Breadcrumbs aria-label='breadcrumb' sx={{ marginBottom: '12px' }}>
        <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='hover' color='inherit' href={RoutePaths.customers.onNavigate()}>
          Customers
        </Link>
        {customer && (
          <Link
            sx={{ textTransform: 'uppercase', fontSize: '12px' }}
            underline='hover'
            color='inherit'
            href={RoutePaths.customersDetails.onNavigate(customer.id)}
          >
            {customer.fullname}
          </Link>
        )}
        {customer && account && (
          <Link
            sx={{ textTransform: 'uppercase', fontSize: '12px' }}
            underline='hover'
            color='inherit'
            href={RoutePaths.customersAccountDetails.onNavigate(customer.id, account.accId)}
          >
            {account.accountNumber_M}
          </Link>
        )}
        <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='none'>
          Transactions
        </Link>
      </Breadcrumbs>
      <Grid container alignContent='center'>
        <DelayedLinearProgress loading={loading} />
        {transactions && (
          <Table aria-label='simple table' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align='left'>Transaction Date</TableCell>
                <TableCell align='center'>Transaction Details</TableCell>
                <TableCell align='center'>Amount (Transaction Currency)</TableCell>
                <TableCell align='center'>Amount (Account Currency)</TableCell>
                <TableCell align='center'>FX Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((row, idx) => (
                <TableRow key={idx} hover>
                  <TableCell align='center'>{DateUtils.formatDate(row.postingDate)}</TableCell>
                  <TableCell align='center'>{row.transactionDescription}</TableCell>
                  <TableCell align='center'>
                    {row.originalCurrency} {row.originalAmount}
                  </TableCell>
                  <TableCell align='center'>
                    {row.accountCurrency} {row.amount}{' '}
                  </TableCell>
                  <TableCell align='center'>{row.exchangeRate} </TableCell>
                </TableRow>
              ))}
              {transactions && !transactions.length ? (
                <TableRow>
                  <TableCell colSpan={5} align='center'>
                    No Records Found
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
      </Grid>
    </>
  );
};
